import { useEffect, useState } from "react";
import { BiometricAuth, BiometryErrorType } from "@aparajita/capacitor-biometric-auth";

// Face ID / Touch ID no app do iPhone.
// Importante: o Face ID aqui TRANCA e DESTRANCA o app (como em apps de banco). Nenhuma senha e guardada.
// Quem usa o Face ID continua logado pela sessao normal; sem usar o app por 7 dias, pede a senha de novo.

export const isNativeApp = () => {
  try {
    return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  } catch (e) {
    return false;
  }
};

const KEY_ON = "ss_bio"; // id da pessoa que ligou o Face ID neste iPhone
const KEY_ASKED = "ss_bio_asked"; // id da pessoa a quem ja perguntamos (para nao perguntar de novo)
const KEY_TOKEN = "ss_token";

const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());

function read(key) {
  try {
    return localStorage.getItem(key) || "";
  } catch (e) {
    return "";
  }
}

function write(key, value) {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch (e) {
    // sem acesso ao armazenamento: segue sem guardar
  }
}

export const getEnabledUser = () => read(KEY_ON);
export const hasSavedLogin = () => !!read(KEY_TOKEN);
export const wasAsked = (userId) => !!userId && read(KEY_ASKED) === userId;

export function setEnabledUser(userId) {
  write(KEY_ON, userId || "");
  emit();
}

export function markAsked(userId) {
  write(KEY_ASKED, userId || "");
  emit();
}

// ---- regras de quando trancar (puras, para poder testar) ----

// Ao abrir o app: tranca se o Face ID esta ligado e existe um login salvo.
export function shouldLockAtStart({ native, enabledUser, hasToken }) {
  return !!(native && enabledUser && hasToken);
}

// Ao voltar para o app: tranca se ficou fora tempo suficiente e o Face ID e dessa mesma pessoa.
export function shouldLockOnResume({ native, enabledUser, userId, awayMs, afterMs }) {
  return !!(native && enabledUser && userId && enabledUser === userId && awayMs >= afterMs);
}

// ---- o que o iPhone tem disponivel ----

function labelFor(type) {
  const text = String(type).toLowerCase();
  if (type === 2 || text.includes("face")) return "Face ID";
  if (type === 1 || text.includes("touch")) return "Touch ID";
  return "Face ID";
}

let infoCache = null;
let infoPromise = null;

export function loadBiometryInfo() {
  if (!isNativeApp()) return Promise.resolve({ ready: true, supported: false, label: "Face ID" });
  if (infoCache) return Promise.resolve(infoCache);
  if (!infoPromise) {
    infoPromise = BiometricAuth.checkBiometry()
      .then((r) => {
        infoCache = { ready: true, supported: !!r.isAvailable, label: labelFor(r.biometryType) };
        return infoCache;
      })
      .catch(() => {
        infoCache = { ready: true, supported: false, label: "Face ID" };
        return infoCache;
      });
  }
  return infoPromise;
}

const isCode = (code, name) => !!code && (code === BiometryErrorType[name] || String(code) === name);

function friendlyMessage(code) {
  if (isCode(code, "biometryLockout")) return "Too many tries. Use your phone passcode to continue.";
  if (isCode(code, "biometryNotEnrolled")) return "Face ID is not set up on this phone.";
  if (isCode(code, "biometryNotAvailable")) return "Face ID is not available right now.";
  if (isCode(code, "noDeviceCredential") || isCode(code, "passcodeNotSet")) return "Set a passcode on this phone to use Face ID.";
  return "That did not work. Please try again.";
}

// Mostra o Face ID do iPhone. Devolve { ok, cancelled, message } e nunca lanca erro.
export async function authenticateUser(reason) {
  try {
    await BiometricAuth.authenticate({
      reason,
      cancelTitle: "Cancel",
      allowDeviceCredential: true, // se o Face ID falhar (luva, mascara, poeira), da para usar a senha do celular
      iosFallbackTitle: "Use passcode",
    });
    return { ok: true, cancelled: false, message: "" };
  } catch (e) {
    const code = e && e.code;
    const cancelled = isCode(code, "userCancel") || isCode(code, "appCancel") || isCode(code, "systemCancel");
    return { ok: false, cancelled, message: cancelled ? "" : friendlyMessage(code) };
  }
}

// Hook para as telas: { ready, supported, label, enabledUser }. Atualiza sozinho quando liga ou desliga.
export function useBiometric() {
  const [info, setInfo] = useState(infoCache || { ready: false, supported: false, label: "Face ID" });
  const [, bump] = useState(0);

  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);

  useEffect(() => {
    let alive = true;
    loadBiometryInfo().then((i) => {
      if (alive) setInfo(i);
    });
    return () => {
      alive = false;
    };
  }, []);

  return { ...info, enabledUser: getEnabledUser() };
}
